import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppLayout, LoadingSpinner } from '../components/Layout';
import {
  DollarSign, Users, Zap, Award, Target, CheckCircle2, AlertTriangle,
  ArrowRight, Building, Clock, GraduationCap, Sun, BookOpen, Rocket,
  Compass, List as ListIcon, GitBranch, Sparkles, CircleDot, Info,
} from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { C, GLOW, GRAD } from './careerTokens';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const stagger = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } };
const fadeUp = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

const PHASE_ICON = { grad: GraduationCap, sun: Sun, book: BookOpen, users: Users, rocket: Rocket };
const PROOF_STATUS = {
  proven: { label: 'Proven', color: C.green },
  building: { label: 'Building', color: C.gold },
  missing: { label: 'Missing', color: C.pink },
};

const proofHint = (item) => {
  const t = item.toLowerCase();
  if (t.includes('sql')) return 'Complete a SQL course and build 2-3 queries/projects.';
  if (t.includes('tableau') || t.includes('visual') || t.includes('power bi')) return 'Create a Tableau or Power BI dashboard project.';
  if (t.includes('project') || t.includes('analytics project')) return 'Analyze a real dataset (e.g. employee turnover).';
  if (t.includes('internship') || t.includes('experience')) return 'Complete an internship or research project in your field.';
  if (t.includes('linkedin') || t.includes('portfolio')) return 'Publish a polished profile or portfolio recruiters can find.';
  if (t.includes('certification')) return 'Earn a recognized certificate and add it to your record.';
  return 'Build a concrete piece of evidence you can show an employer.';
};

export default function CareerIntelligencePage() {
  const { token } = useAuth();
  const authH = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [data, setData] = useState(null);
  const [employers, setEmployers] = useState([]);
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [routeList, setRouteList] = useState(false);

  useEffect(() => {
    if (!token) return;
    Promise.all([
      axios.get(`${API}/college/career-intelligence`, authH),
      axios.get(`${API}/college/employer-targets`, authH).catch(() => ({ data: null })),
      axios.get(`${API}/opportunities/ranked`, authH).catch(() => ({ data: null })),
    ]).then(([intel, emp, op]) => {
      setData(intel.data);
      setEmployers((emp.data?.companies || []).sort((a, b) => b.fit_score - a.fit_score).slice(0, 5));
      setOpps((op.data?.opportunities || []).slice(0, 4));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token, authH]);

  if (loading) return <AppLayout><div className="flex items-center justify-center min-h-[60vh]"><LoadingSpinner size="lg" /></div></AppLayout>;
  if (!data) return <AppLayout><div className="text-center py-20" style={{ color: C.muted }}>Unable to load Career Plan.</div></AppLayout>;

  const { student, verdict, route_through_graduation: route, strengths, capability_gaps, ai_outlook,
    role_fit, launch_readiness, reality_check, proof_requirements, salary_context } = data;

  const salaryRange = salary_context?.entry_level?.range || 'Not enough verified data yet';
  const competition = reality_check?.find(r => (r.label || '').includes('Competitiveness'))?.rating || 'Moderate';

  // Proof cards from the same proof engine (no duplicate data)
  const proofItems = [];
  ['technical', 'applied', 'professional'].forEach(cat => {
    (proof_requirements?.categories?.[cat] || []).forEach(it => {
      if (it.priority === 'critical' || it.priority === 'high') proofItems.push(it);
    });
  });
  const proofCards = proofItems.slice(0, 4).map(it => ({
    title: it.item, status: it.status === 'claimed' ? 'proven' : 'missing', hint: proofHint(it.item),
  }));

  const aiTone = ai_outlook?.tone === 'warn' ? C.gold : ai_outlook?.tone === 'muted' ? C.muted : C.green;

  return (
    <AppLayout>
      <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-5 max-w-[1440px] mx-auto" data-testid="career-plan-page">

        {/* HEADER */}
        <motion.div variants={fadeUp}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-outfit font-bold text-white" style={{ fontSize: 'clamp(26px,3vw,34px)' }}>Career Plan</h1>
                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider" style={{ color: C.purple, background: `${C.purple}1F`, border: `1px solid ${C.purple}55` }}>Beta</span>
              </div>
              <p className="text-sm mt-1" style={{ color: C.text2 }}>Pressure-test your target and map your route through graduation.</p>
            </div>
            <div className="hidden lg:flex items-center gap-x-4 gap-y-1 text-[12px] flex-wrap justify-end max-w-md">
              <Meta k="Institution" v={student.institution || 'Rutgers-Camden'} />
              <Meta k="Major" v={student.major} />
              <Meta k="Target" v={student.career_target} accent />
              <Meta k="Year" v={student.academic_year} />
              <Meta k="GPA" v={student.gpa} />
              {route?.grad_year && <Meta k="Grad" v={route.grad_year} />}
            </div>
          </div>
        </motion.div>

        {/* HERO: verdict + metrics */}
        <motion.div variants={fadeUp}>
          <div className="relative overflow-hidden rounded-2xl p-6" style={{ background: GRAD.hero, border: `1px solid ${C.cyan}44`, boxShadow: GLOW.hero }}>
            <div aria-hidden className="pointer-events-none absolute -top-16 -right-10 w-64 h-64 rounded-full blur-3xl opacity-15" style={{ background: `radial-gradient(circle, ${C.cyan}, transparent 65%)` }} />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-5">
                <h2 className="font-outfit font-bold text-white text-2xl">{student.is_hs ? `Grade ${student.grade_level || '12'}` : student.major} <span style={{ color: C.muted }}>→</span> {student.career_target}</h2>
                <div className="flex items-center gap-2 mt-3">
                  <CheckCircle2 className="w-5 h-5" style={{ color: verdict?.tone === 'stretch' ? C.gold : C.green }} />
                  <span className="font-outfit font-bold text-white text-lg" data-testid="verdict-status">{verdict?.status}</span>
                </div>
                <p className="text-[13px] mt-2 leading-relaxed max-w-lg" style={{ color: C.text2 }} data-testid="verdict-summary">{verdict?.summary}</p>
                <div className="flex flex-wrap gap-2 mt-5">
                  <Link to="/app/my-plan" data-testid="build-semester-plan" className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white" style={{ background: GRAD.cta, boxShadow: GLOW.cyan }}>
                    <Target className="w-4 h-4" /> Build My Semester Plan <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/app/career-landscape" data-testid="explore-other-careers" className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold" style={{ color: C.text2, border: `1px solid ${C.border}66` }}>
                    <Compass className="w-4 h-4" /> Explore Other Careers
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-7">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                  <Ring label="Role Fit" value={role_fit?.score} sub={role_fit?.label} color={C.cyan} tip={role_fit?.explanation} />
                  <Ring label="Readiness" value={launch_readiness?.score} sub={launch_readiness?.label} color={C.purple} tip={launch_readiness?.explanation} />
                  <Flat icon={DollarSign} color={C.green} value={salaryRange} label="Salary Range" sub={salary_context?.entry_level?.note?.split('.')[0]} />
                  <Flat icon={Users} color={C.gold} value={competition} label="Competition" sub="Hiring is steady" />
                  <Flat icon={Zap} color={aiTone} value={ai_outlook?.label} label="AI Outlook" sub={ai_outlook?.detail} clip link="/app/future-of-work" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ROUTE THROUGH GRADUATION */}
        <motion.div variants={fadeUp}>
          <div className="rounded-2xl p-6" style={{ background: C.surface, border: `1px solid ${C.border}66` }} data-testid="route-through-graduation">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-outfit font-bold text-white text-lg flex items-center gap-2"><GitBranch className="w-5 h-5" style={{ color: C.cyan }} /> Your Route Through Graduation</h2>
              {route?.available && (
                <button onClick={() => setRouteList(v => !v)} data-testid="route-view-toggle" className="flex items-center gap-1.5 text-[12px]" style={{ color: C.cyan }}>
                  <ListIcon className="w-3.5 h-3.5" /> View as {routeList ? 'timeline' : 'list'}
                </button>
              )}
            </div>
            <p className="text-[12px] mb-5" style={{ color: C.muted }}>A personalized timeline to build the skills, experience, and proof you need for {student.career_target}.</p>

            {!route?.available ? (
              <div className="rounded-xl p-5 text-center" style={{ background: C.surface2, border: `1px dashed ${C.border}` }}>
                <Info className="w-5 h-5 mx-auto mb-2" style={{ color: C.muted }} />
                <p className="text-[13px]" style={{ color: C.text2 }}>{route?.message}</p>
                <Link to="/app/profile" className="text-[12px] font-semibold mt-2 inline-block" style={{ color: C.cyan }}>Add academic details →</Link>
              </div>
            ) : routeList ? (
              <div className="space-y-3">
                {route.phases.map((p, i) => {
                  const Icon = PHASE_ICON[p.icon] || CircleDot;
                  return (
                    <div key={i} className="flex items-start gap-3 rounded-xl p-3" style={{ background: C.surface2, border: `1px solid ${C.border}44` }}>
                      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${p.color}1A`, border: `1px solid ${p.color}55` }}><Icon className="w-4 h-4" style={{ color: p.color }} /></span>
                      <div>
                        <div className="flex items-center gap-2"><p className="text-white font-semibold text-sm">{p.label}</p>{p.current && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ color: C.cyan, background: `${C.cyan}1A` }}>CURRENT</span>}</div>
                        <ul className="mt-1 space-y-0.5">{p.actions.map((a, j) => <li key={j} className="text-[12px] flex items-start gap-1.5" style={{ color: C.text2 }}><span className="w-1 h-1 rounded-full mt-1.5" style={{ background: p.color }} />{a}</li>)}</ul>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="relative">
                <div className="absolute top-6 left-6 right-6 h-0.5 rounded-full hidden lg:block" style={{ background: `linear-gradient(90deg, ${C.green}, ${C.gold}, ${C.cyan}, ${C.purple})`, opacity: 0.4 }} />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 relative">
                  {route.phases.map((p, i) => {
                    const Icon = PHASE_ICON[p.icon] || CircleDot;
                    return (
                      <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} data-testid={`route-phase-${i}`}>
                        <div className="flex flex-col items-center text-center mb-2">
                          <span className="w-12 h-12 rounded-xl flex items-center justify-center relative z-10" style={{ background: C.surface, border: `2px solid ${p.color}`, boxShadow: p.current ? `0 0 16px ${p.color}66` : 'none' }}><Icon className="w-5 h-5" style={{ color: p.color }} /></span>
                          <p className="text-white font-semibold text-[13px] mt-2">{p.label}</p>
                          {p.current && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded mt-0.5" style={{ color: C.cyan, background: `${C.cyan}1A` }}>CURRENT</span>}
                        </div>
                        <div className="rounded-xl p-3" style={{ background: C.surface2, border: `1px solid ${p.color}33` }}>
                          <ul className="space-y-1.5">{p.actions.map((a, j) => <li key={j} className="text-[11.5px] flex items-start gap-1.5" style={{ color: C.text2 }}><span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: p.color }} />{a}</li>)}</ul>
                          <p className="text-[9px] font-bold uppercase tracking-wider mt-2" style={{ color: p.color }}>{p.badge}</p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* PROVE + WHY FITS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <motion.div variants={fadeUp}>
            <div className="rounded-2xl p-5 h-full" style={{ background: C.surface, border: `1px solid ${C.border}66` }} data-testid="what-to-prove">
              <h3 className="font-outfit font-bold text-white text-base">What You Need to Prove</h3>
              <p className="text-[11px] mb-4" style={{ color: C.muted }}>Key skills and experiences to strengthen your candidacy.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {proofCards.length === 0 && <p className="text-[12px]" style={{ color: C.muted }}>Set a career target to see proof targets.</p>}
                {proofCards.map((c, i) => {
                  const st = PROOF_STATUS[c.status];
                  return (
                    <div key={i} className="rounded-xl p-3.5" style={{ background: C.surface2, border: `1px solid ${st.color}33` }}>
                      <div className="flex items-center justify-between mb-1.5">
                        <Award className="w-4 h-4" style={{ color: st.color }} />
                        <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: st.color, background: `${st.color}1A`, border: `1px solid ${st.color}44` }}>{st.label}</span>
                      </div>
                      <p className="text-white font-semibold text-[13px] leading-tight">{c.title}</p>
                      <p className="text-[11px] mt-1.5" style={{ color: C.muted }}>Proof: {c.hint}</p>
                    </div>
                  );
                })}
              </div>
              <Link to="/app/proof-builder" className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold" style={{ color: C.cyan }}>Open Proof Builder <ArrowRight className="w-3 h-3" /></Link>
            </div>
          </motion.div>

          <motion.div variants={fadeUp}>
            <div className="rounded-2xl p-5 h-full" style={{ background: C.surface, border: `1px solid ${C.border}66` }} data-testid="why-fits-you">
              <h3 className="font-outfit font-bold text-white text-base">Why This Fits You</h3>
              <p className="text-[11px] mb-4" style={{ color: C.muted }}>Your unique strengths and where to focus next.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-xl p-3.5" style={{ background: `${C.green}0D`, border: `1px solid ${C.green}33` }}>
                  <p className="text-[12px] font-bold flex items-center gap-1.5 mb-2" style={{ color: C.green }}><CheckCircle2 className="w-4 h-4" /> Strengths Already Working for You</p>
                  <ul className="space-y-1.5">{strengths?.map((s, i) => <li key={i} className="text-[11.5px] flex items-start gap-1.5" style={{ color: C.text2 }}><span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: C.green }} />{s}</li>)}</ul>
                </div>
                <div className="rounded-xl p-3.5" style={{ background: `${C.pink}0D`, border: `1px solid ${C.pink}33` }}>
                  <p className="text-[12px] font-bold flex items-center gap-1.5 mb-2" style={{ color: C.pink }}><AlertTriangle className="w-4 h-4" /> Biggest Capability Gaps</p>
                  <ul className="space-y-1.5">{capability_gaps?.map((g, i) => <li key={i} className="text-[11.5px] flex items-start gap-1.5" style={{ color: C.text2 }}><span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: C.pink }} />{g}</li>)}</ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* EMPLOYERS + OPPORTUNITIES */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <motion.div variants={fadeUp}>
            <div className="rounded-2xl p-5 h-full" style={{ background: C.surface, border: `1px solid ${C.border}66` }} data-testid="who-hires">
              <h3 className="font-outfit font-bold text-white text-base">Who Hires for This</h3>
              <p className="text-[11px] mb-4" style={{ color: C.muted }}>Employers hiring {student.career_target} talent and adjacent roles.</p>
              {employers.length === 0 ? (
                <p className="text-[12px]" style={{ color: C.muted }}>Not enough verified employer data yet for this target.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {employers.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 rounded-xl px-3 py-2" style={{ background: C.surface2, border: `1px solid ${C.border}44` }}>
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${C.cyan}12`, border: `1px solid ${C.cyan}33` }}><Building className="w-3.5 h-3.5" style={{ color: C.cyan }} /></span>
                      <div><p className="text-[12px] text-white font-semibold leading-tight">{e.name}</p><p className="text-[9px]" style={{ color: C.muted }}>{e.type}</p></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          <motion.div variants={fadeUp}>
            <div className="rounded-2xl p-5 h-full" style={{ background: C.surface, border: `1px solid ${C.border}66` }} data-testid="opps-for-target">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-outfit font-bold text-white text-base">Opportunities for This Target</h3>
                <Link to="/app/internships" className="text-[12px] font-semibold" style={{ color: C.cyan }}>View all →</Link>
              </div>
              <p className="text-[11px] mb-4" style={{ color: C.muted }}>Curated to help you build experience and stand out.</p>
              {opps.length === 0 ? (
                <p className="text-[12px]" style={{ color: C.muted }}>No opportunity records match your profile yet, we won't invent any. Check back as records are added.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {opps.map((o, i) => (
                    <div key={i} className="rounded-xl p-3" style={{ background: C.surface2, border: `1px solid ${C.border}44` }}>
                      <div className="flex items-center gap-1.5 mb-1"><Rocket className="w-3.5 h-3.5" style={{ color: C.purple }} /><span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: C.muted }}>{o.type || 'Opportunity'}</span></div>
                      <p className="text-white font-semibold text-[12.5px] leading-tight">{o.title}</p>
                      <p className="text-[10.5px] mt-0.5" style={{ color: C.muted }}>{o.provider || ''}{o.paid ? ' · Paid' : ''}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* ALTERNATIVE LINK */}
        <motion.div variants={fadeUp}>
          <div className="rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap" style={{ background: C.surface, border: `1px solid ${C.border}55` }}>
            <p className="text-[13px]" style={{ color: C.text2 }}>Not sure this is the right target?</p>
            <Link to="/app/career-landscape" className="inline-flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: C.cyan }}>Explore other careers <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
        </motion.div>
      </motion.div>
    </AppLayout>
  );
}

function Meta({ k, v, accent }) {
  return <span className="flex items-center gap-1.5"><span style={{ color: C.muted }}>{k}</span><span className="font-semibold capitalize" style={{ color: accent ? C.cyan : C.text }}>{v || '-'}</span></span>;
}

function Ring({ label, value, sub, color, tip }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="rounded-xl p-3 text-center relative group" style={{ background: C.surface2, border: `1px solid ${C.border}44` }}>
      <div className="w-14 h-14 mx-auto relative">
        <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
          <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="3" />
          <motion.circle cx="18" cy="18" r="15.5" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
            initial={{ strokeDasharray: '0 97.4' }} animate={{ strokeDasharray: `${pct * 0.974} 97.4` }} transition={{ duration: 1 }} style={{ filter: `drop-shadow(0 0 5px ${color}88)` }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-base font-outfit font-black" style={{ color }}>{value}</span><span className="text-[7px]" style={{ color: C.muted }}>/100</span></div>
      </div>
      <p className="text-[10px] mt-1" style={{ color: C.text2 }}>{label}</p>
      <p className="text-[10px] font-semibold" style={{ color }}>{sub}</p>
      {tip && <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-52 p-2.5 rounded-lg text-left opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-30" style={{ background: C.bgDeep, border: `1px solid ${C.cyan}33` }}><p className="text-[10px]" style={{ color: C.text2 }}>{tip}</p></div>}
    </div>
  );
}

function Flat({ icon: Icon, color, value, label, sub, clip, link }) {
  const body = (
    <div className="rounded-xl p-3 text-center h-full" style={{ background: C.surface2, border: `1px solid ${C.border}44` }}>
      <Icon className="w-4 h-4 mx-auto mb-1" style={{ color }} />
      <p className="font-outfit font-bold leading-tight" style={{ color, fontSize: value && value.length > 10 ? 12 : 15 }}>{value}</p>
      <p className="text-[10px] mt-1" style={{ color: C.text2 }}>{label}</p>
      {sub && <p className={`text-[8.5px] mt-0.5 ${clip ? 'line-clamp-2' : ''}`} style={{ color: C.muted }}>{sub}</p>}
    </div>
  );
  return link ? <Link to={link}>{body}</Link> : body;
}
